import { useContext, useEffect, useState } from 'react'

import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'

import { Divider } from '@mui/material'
import { PlanningBasics } from '../../../api'
import CheckIcon from '../../../assets/check.svg'
import { AppStateContext } from '../../../state/AppProvider'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { BinaryRadioFormField } from '../../form/BinaryRadioFormField'
import { SelectFormField } from '../../form/SelectFormField'
import { TextFormField } from '../../form/TextFormField'
import styles from './PlanningBasicsStep.module.css'

interface Props {
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const PlanningBasicsStep = ({ onError }: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [data, setData] = useState<PlanningBasics | null>(null)
  const [convId, setConvId] = useState<string>('')
  const select_gla_strategic_priority_url = appStateContext?.state.frontendSettings?.template?.select_gla_strategic_priority

  // Retrieve the current chat data
  useEffect(() => {
    if (appStateContext?.state.currentChat?.extended_data?.experiment?.planning_basics) {
      setConvId(appStateContext?.state.currentChat?.id || '')
      setData(appStateContext?.state.currentChat?.extended_data?.experiment?.planning_basics)
    }
  }, [appStateContext?.state.currentChat?.extended_data])

  if (!data) {
    return <Typography>Loading ... or Error</Typography>
  }

  return (
    <Grid
      container
      columnSpacing={2}
      rowSpacing={4}>
      <TextFormField
        name="Updated By"
        stepName="planning_basics"
        fieldName="updated_by"
        convId={convId}
        gridSize={6}
        data={data.updated_by_name}
        disabled
        onError={onError}
      />
      <SelectFormField
        name="Year"
        stepName="planning_basics"
        fieldName="year"
        convId={convId}
        gridSize={6}
        data={data?.year}
        tooltipInfo={
          <>
            Select the year that most live dates fall into if they span over <b>two</b> years
          </>
        }
        disabled={data?.year?.disabled}
        onError={onError}
      />
      <SelectFormField
        name="Quarter"
        stepName="planning_basics"
        fieldName="quarter"
        convId={convId}
        gridSize={6}
        data={data?.quarter}
        disabled={data?.quarter?.disabled}
        tooltipInfo={
          <>
            Select the quarter that most live dates fall into if they span over <b>two</b> quarters
          </>
        }
        onError={onError}
      />
      <SelectFormField
        name="OU"
        stepName="planning_basics"
        fieldName="ou"
        convId={convId}
        gridSize={6}
        data={data?.ou}
        disabled={data?.ou?.disabled}
        onError={onError}
      />
      <SelectFormField
        name="Country"
        stepName="planning_basics"
        fieldName="country"
        convId={convId}
        gridSize={6}
        data={data?.country}
        disabled={data?.country?.disabled}
        onError={onError}
      />
      <SelectFormField
        name="Global Category Name"
        stepName="planning_basics"
        fieldName="global_category_name"
        convId={convId}
        gridSize={6}
        data={data?.global_category_name}
        disabled={data?.global_category_name?.disabled}
        onError={onError}
      />
      <SelectFormField
        name="Brand"
        stepName="planning_basics"
        fieldName="brand"
        convId={convId}
        gridSize={6}
        data={data?.brand}
        disabled={data?.brand?.disabled}
        onError={onError}
      />
      <SelectFormField
        name="Bottler"
        stepName="planning_basics"
        fieldName="bottler"
        convId={convId}
        gridSize={6}
        data={data?.bottler}
        disabled={data?.bottler?.disabled}
        onError={onError}
      />
      <BinaryRadioFormField
        stepName="planning_basics"
        fieldName="dme_and_resources_committed"
        convId={convId}
        name="DME and Resources Committed"
        gridSize={12}
        data={data?.dme_and_resources_committed}
        disabled={data?.dme_and_resources_committed?.disabled}
        onError={onError}
      />
      <Divider className={styles.divider} />
      <BinaryRadioFormField
        stepName="planning_basics"
        fieldName="is_e_at_s_experiment"
        convId={convId}
        name="E@S Experiment"
        gridSize={12}
        data={data?.is_e_at_s_experiment}
        disabled={data?.is_e_at_s_experiment?.disabled}
        onError={onError}
      />
      {data?.mto_initiative && (
        <SelectFormField
          name="Select GLA Strategic Priority"
          stepName="planning_basics"
          fieldName="mto_initiative"
          convId={convId}
          gridSize={12}
          data={data?.mto_initiative}
          url={select_gla_strategic_priority_url}
          disabled={data?.mto_initiative?.disabled}
          onError={onError}
        />
      )}
      {data?.key_question && (
        <SelectFormField
          name="Key Question"
          stepName="planning_basics"
          fieldName="key_question"
          convId={convId}
          gridSize={12}
          data={data?.key_question}
          disabled={data?.key_question?.disabled}
          onError={onError}
        />
      )}
      {data?.hypothesis_template && (
        <SelectFormField
          name="Hypothesis Template"
          stepName="planning_basics"
          fieldName="hypothesis_template"
          convId={convId}
          gridSize={12}
          data={data?.hypothesis_template}
          disabled={data?.hypothesis_template?.disabled}
          onError={onError}
        />
      )}
      <TextFormField
        name="Hypothesis"
        stepName="planning_basics"
        fieldName="hypothesis"
        convId={convId}
        data={data?.hypothesis}
        gridSize={12}
        disabled={data?.hypothesis?.disabled}
        multiline
        rows={4}
        onError={onError}
      />
      <SelectFormField
        name="Global Charter"
        stepName="planning_basics"
        fieldName="global_charter"
        convId={convId}
        gridSize={6}
        data={data?.global_charter}
        disabled={data?.global_charter?.disabled}
        onError={onError}
      />
      <TextFormField
        name="Experiment Code"
        stepName="planning_basics"
        fieldName="experiment_code"
        convId={convId}
        gridSize={6}
        rows={1}
        disabled={data?.experiment_code?.disabled}
        data={data?.experiment_code}
        validIcon={
          <img
            src={CheckIcon}
            alt="Check"
          />
        }
        onError={onError}
      />
      {data?.local_charter && (
        <TextFormField
          name="Local Charter"
          stepName="planning_basics"
          fieldName="local_charter"
          convId={convId}
          gridSize={12}
          rows={1}
          data={data?.local_charter}
          disabled={data?.local_charter?.disabled}
        />
      )}
      <SelectFormField
        name="Methodology"
        stepName="planning_basics"
        fieldName="methodology"
        convId={convId}
        gridSize={6}
        data={data?.methodology}
        disabled={data?.methodology?.disabled}
      />
      <SelectFormField
        name="Leading KPI"
        stepName="planning_basics"
        fieldName="leading_kpi"
        convId={convId}
        gridSize={6}
        data={data?.leading_kpi}
        disabled={data?.leading_kpi?.disabled}
      />

      <SelectFormField
        name="Experiment Phase: High Level"
        stepName="planning_basics"
        fieldName="experiment_phase_high_level"
        convId={convId}
        gridSize={12}
        data={data?.experiment_phase_high_level}
        disabled={data?.experiment_phase_high_level?.disabled}
      />
      <SelectFormField
        name="Action Statement"
        stepName="planning_basics"
        fieldName="action_statement"
        convId={convId}
        gridSize={12}
        data={data?.action_statement}
        tooltipInfo="What is the next step if the hypothesis is proven with sufficient confidence?"
        disabled={data?.action_statement?.disabled}
      />
    </Grid>
  )
}
